import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { formatApiError } from "@/lib/api";
import { inputCls, btnPrimary, Field } from "@/components/shared";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" /><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.96 10.96 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" /></svg>
);

export default function Login() {
  const { user, login, register, googleLogin } = useAuth();
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user && !user.is_guest) return <Navigate to="/" replace />;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const signup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (signup) await register(form.name.trim(), form.email.trim(), form.password);
      else await login(form.email.trim(), form.password);
      toast.success(signup ? "Welcome! Your account is ready" : "Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src={theme.hero} alt="Calm scene" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14 text-white">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-white/70 mb-3">Base and Balance</p>
          <p className="font-serif-display text-5xl font-semibold leading-tight">Your home, your lists, your pace.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" data-testid="login-back-link" className="inline-flex items-center gap-1 text-sm text-t-muted hover:text-t-ink mb-10"><ArrowLeft className="h-4 w-4" /> Back to the app</Link>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold" data-testid="login-title">{signup ? "Create your account" : "Welcome back"}</h1>
          <p className="text-sm text-t-muted mt-2 mb-8">
            {signup ? "Anything you made as a guest comes with you." : "Sign in to pick up where you left off."}
          </p>
          <button type="button" data-testid="google-login-button" onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-t-line bg-t-card hover:bg-t-surface text-sm font-medium transition-colors">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6 text-xs text-t-faint"><span className="h-px flex-1 bg-t-line" />or with email<span className="h-px flex-1 bg-t-line" /></div>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {signup && <Field label="Name"><input data-testid="auth-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" required /></Field>}
            <Field label="Email"><input data-testid="auth-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" required /></Field>
            <Field label="Password"><input data-testid="auth-password-input" type="password" className={inputCls} value={form.password} onChange={set("password")} placeholder={signup ? "At least 6 characters" : "Your password"} minLength={signup ? 6 : undefined} required /></Field>
            {!signup && (
              <div className="flex justify-end -mt-2">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-t-primary hover:underline">Forgot password?</Link>
              </div>
            )}
            {error && <p data-testid="auth-error" className="text-sm text-t-danger">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-button" className={`${btnPrimary} w-full py-3`}>{busy ? "One moment..." : signup ? "Create account" : "Sign in"}</button>
          </form>
          <p className="text-sm text-t-muted mt-6 text-center">
            {signup ? "Already have an account?" : "New here?"}{" "}
            <button data-testid="auth-mode-toggle" onClick={() => { setMode(signup ? "signin" : "signup"); setError(""); }} className="text-t-primary font-medium hover:underline">
              {signup ? "Sign in" : "Create an account"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
